import React, { useEffect, useState, useRef } from "react";
import { X, Trash2, Plus, Copy, Activity, FlaskConical } from "lucide-react";
import CoachStrengthLogView from "./CoachStrengthLogView";
import {
  WORKOUT_TYPES,
  smartSegmentsFor,
  mkSimple,
  mkGroup,
  mkWork,
  mkRest,
  workoutVolume,
  RUNNING_TYPES,
} from "../../lib/coaching";
import { RichTextEditor } from "../RichTextEditor";
import { extractDurationOrDistance } from "../../lib/workout_title";
import { SegmentRow, reorderList } from "./SegmentEditor";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import StrengthEditor from "./StrengthEditor";

// Workout types that use the block-style editor (exercises with sets/reps,
// supersets, circuits) instead of the run-style segment timeline. Mobility
// behaves like strength conceptually — a list of exercises with rep counts —
// so we route both through the same editor & athlete logger.
const BLOCK_TYPES = new Set(["strength", "mobility"]);

export default function WorkoutEditor({ workout, onSave, onClose, onDelete, onDuplicate, currentDayLabel, weekDays, athletePaces, paceSource, athleteId, programId, allWeeks, currentWeekNumber }) {
  const isBlockType = (t) => BLOCK_TYPES.has(t);
  // Planner workouts carry the coach's free text ("7km Easy + Strides") in
  // duration_or_distance with no title. On open, migrate: full text → Title
  // (optional override), extracted quantity ("7 km") → duration field. If no
  // quantity is extractable ("Speed Run 2:3"), duration is left blank for
  // the coach to fill manually.
  const [init] = useState(() => {
    const base = { ...workout };
    const rawText = String(base.duration_or_distance || "").trim();
    let autoFilled = null;
    if (!String(base.title || "").trim() && rawText) {
      const guess = extractDurationOrDistance(rawText);
      base.title = rawText;
      base.duration_or_distance = guess || "";
      autoFilled = guess || null;
    }
    return { base, autoFilled };
  });
  const [form, setForm] = useState(() => ({
    ...init.base,
    segments: workout.segments && workout.segments.length
      ? workout.segments
      : isBlockType(workout.type) ? [] : smartSegmentsFor(workout.type, init.base),
    // Strength / mobility: NO auto-seeded "3×8-10" block. Coach builds the
    // blocks themselves (per their request — the auto-block was bleeding
    // into emails and PDFs even when deleted-then-reopened). We preserve
    // whatever the workout came in with, but if none, leave it empty.
    strength_blocks: workout.strength_blocks || [],
  }));
  // Track which types have been "initialized" — so changing type reseeds defaults
  // but editing segments on the current type doesn't.
  const [seededType, setSeededType] = useState(workout.type);

  // Track the last value that Title auto-filled into duration_or_distance
  // so we can safely UPDATE it as the coach types character-by-character
  // ("1h" → "1h30" → "1h30m") without clobbering a value the coach
  // manually typed after the auto-fill. Ref (not state) because we
  // don't need re-renders — the guard is checked inline in onChange.
  // Seeded with the value the planner-text migration auto-filled (if any).
  const autoFilledDurationRef = useRef(init.autoFilled);

  useEffect(() => {
    if (seededType !== form.type) {
      setForm((f) => ({
        ...f,
        segments: isBlockType(f.type) ? [] : smartSegmentsFor(f.type, f),
        // Same rule on type change: keep existing strength_blocks if any
        // (user might have switched away then back); never auto-seed.
        strength_blocks: f.strength_blocks || [],
      }));
      setSeededType(form.type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.type]);

  if (!workout) return null;

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const updateSegment = (idx, updated) => {
    const segments = form.segments.map((s, i) => (i === idx ? updated : s));
    setField("segments", segments);
  };

  const onSegmentDragEnd = (result) => {
    const { source, destination, type } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    if (type === "segment") {
      setField("segments", reorderList(form.segments, source.index, destination.index));
      return;
    }
    // Item inside a repeat group (same group only — groups have distinct ids).
    if (type === "group-item" && source.droppableId === destination.droppableId) {
      const gid = source.droppableId.replace(/^group-/, "");
      const idx = form.segments.findIndex((sg) => sg.id === gid);
      if (idx < 0) return;
      const grp = form.segments[idx];
      updateSegment(idx, { ...grp, items: reorderList(grp.items, source.index, destination.index) });
    }
  };

  const deleteSegment = (idx) => {
    setField("segments", form.segments.filter((_, i) => i !== idx));
  };

  const addSegment = (kind) => {
    const next =
      kind === "group"
        ? mkGroup({ items: [mkWork(), mkRest()] })
        : kind === "warmup"
          ? mkSimple({ label: "Warm-up", value: "10", unit: "min", zones: [1] })
          : kind === "cooldown"
            ? mkSimple({ label: "Cool-down", value: "10", unit: "min", zones: [1] })
            : mkSimple();
    setField("segments", [...form.segments, next]);
  };

  const save = () => {
    onSave({
      ...form,
      segments: form.segments,
      // Pass the count through verbatim so ranges like "4-6" survive.
      // null when blank or when add_strides is off.
      strides_count: form.add_strides
        ? (form.strides_count === "" ? null : form.strides_count)
        : null,
      strides_kind: form.add_strides ? (form.strides_kind || "strides") : null,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4"
      data-testid="workout-editor"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-3xl bg-[var(--tir-ivory)] border border-[var(--tir-ink)] shadow-2xl max-h-[94vh] overflow-y-auto rounded-t-lg sm:rounded-none">
        <div className="flex items-start justify-between p-4 sm:p-6 border-b border-[var(--tir-border)] sticky top-0 bg-[var(--tir-ivory)] z-10">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold">
              Workout
            </div>
            <h3 className="font-serif-display text-xl sm:text-2xl text-[var(--tir-ink)] leading-tight">
              Build the session
            </h3>
            {paceSource && paceSource.kind && (
              <div
                className={`mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[10px] tracking-[0.18em] uppercase font-semibold ${
                  paceSource.kind === "lactate"
                    ? "bg-[var(--tir-burgundy)]/10 text-[var(--tir-burgundy)] border border-[var(--tir-burgundy)]/30"
                    : paceSource.kind === "vdot"
                      ? "bg-[var(--tir-navy)]/10 text-[var(--tir-navy)] border border-[var(--tir-navy)]/30"
                      : "bg-black/[0.04] text-[var(--tir-muted)] border border-[var(--tir-border)]"
                }`}
                data-testid="pace-source-badge"
                title={
                  paceSource.kind === "lactate"
                    ? "Suggested paces auto-populate from this athlete's most recent lactate test"
                    : paceSource.kind === "vdot"
                      ? "Suggested paces come from a VDOT estimate (no lactate test on file)"
                      : "No pace data available — log a lactate test or set a recent race result"
                }
              >
                {paceSource.kind === "lactate" ? (
                  <FlaskConical size={11} strokeWidth={2.4} />
                ) : (
                  <Activity size={11} strokeWidth={2.4} />
                )}
                <span>
                  {paceSource.kind === "lactate"
                    ? `Lactate test${paceSource.date ? ` · ${paceSource.date}` : ""}`
                    : paceSource.kind === "vdot"
                      ? "VDOT estimate"
                      : "No paces on file"}
                </span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-[var(--tir-muted)] hover:text-[var(--tir-ink)] p-1"
            aria-label="Close"
            data-testid="workout-editor-close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-5">
          {/* Top form: type / slot / title / total time */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Type
              </label>
              <select
                value={form.type}
                onChange={(e) => setField("type", e.target.value)}
                data-testid="workout-type-select"
                className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-base focus:outline-none"
              >
                {WORKOUT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Slot
              </label>
              <select
                value={form.slot || "any"}
                onChange={(e) => setField("slot", e.target.value)}
                className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-base focus:outline-none"
              >
                <option value="any">Anytime</option>
                <option value="am">AM</option>
                <option value="pm">PM</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Title (optional override)
              </label>
              <input
                value={form.title || ""}
                onChange={(e) => {
                  const nextTitle = e.target.value;
                  setField("title", nextTitle);
                  // Auto-populate Total time/distance from what the
                  // coach types in the title. Two-way guard so we
                  // don't overwrite manual edits:
                  //  • Empty duration → always auto-fill from extraction.
                  //  • Non-empty AND we're the ones who auto-filled that
                  //    exact value → update to the fresh extraction
                  //    (handles typing "1h" → "1h30" incrementally).
                  //  • Non-empty AND value differs from our last
                  //    auto-fill → coach edited it manually, leave alone.
                  const currentDur = String(form.duration_or_distance || "");
                  const guess = extractDurationOrDistance(nextTitle);
                  const canReplace =
                    currentDur.trim() === "" ||
                    currentDur === autoFilledDurationRef.current;
                  if (canReplace) {
                    if (guess) {
                      setField("duration_or_distance", guess);
                      autoFilledDurationRef.current = guess;
                    } else if (currentDur === autoFilledDurationRef.current) {
                      // Coach cleared the number from the title — clear
                      // the auto-filled duration too so we don't leave
                      // a stale value behind.
                      setField("duration_or_distance", "");
                      autoFilledDurationRef.current = null;
                    }
                  }
                }}
                placeholder='e.g. "45 min Easy" or "10 km Long Run"'
                data-testid="workout-title-input"
                autoFocus
                className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-sm focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Total time or distance (optional summary)
              </label>
              <input
                value={form.duration_or_distance || ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setField("duration_or_distance", v);
                  // Coach manually edited the duration → break the
                  // link so future Title edits don't overwrite it.
                  if (v !== autoFilledDurationRef.current) {
                    autoFilledDurationRef.current = null;
                  }
                }}
                placeholder="60 min  —or—  10 km"
                data-testid="workout-duration-input"
                className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-sm focus:outline-none font-mono-tir"
              />
              <PaceEstimateHint form={form} athletePaces={athletePaces} />
            </div>
          </div>

          {/* "Alternative option" toggle — turns this workout into an OR-of-the-previous */}
          <label
            className="flex items-start gap-3 px-3 py-2 bg-white border border-[var(--tir-border)] cursor-pointer hover:border-[var(--tir-navy)] transition-colors"
            data-testid="workout-alternative-toggle"
          >
            <input
              type="checkbox"
              checked={!!form.is_alternative}
              onChange={(e) => setField("is_alternative", e.target.checked)}
              className="h-4 w-4 mt-0.5 accent-[var(--tir-burgundy)]"
            />
            <span>
              <span className="block text-[11px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-ink)]">
                Alternative option (OR)
              </span>
              <span className="block text-[11px] text-[var(--tir-muted)] mt-0.5 leading-snug">
                Mark this as a backup choice the athlete can pick instead of the previous workout
                on the same day (e.g. "Group run OR Hill workout treadmill"). Show on portal &amp; PDF as an OR option.
              </span>
            </span>
          </label>

          {/* "Optional" toggle — workout doesn't count toward prescribed totals
              or missed counts. If the athlete completes it, the actual km / time
              / completion still credit the report. */}
          <label
            className="flex items-start gap-3 px-3 py-2 bg-white border border-[var(--tir-border)] cursor-pointer hover:border-[var(--tir-navy)] transition-colors"
            data-testid="workout-optional-toggle"
          >
            <input
              type="checkbox"
              checked={!!form.is_optional}
              onChange={(e) => setField("is_optional", e.target.checked)}
              className="h-4 w-4 mt-0.5 accent-[var(--tir-burgundy)]"
            />
            <span>
              <span className="block text-[11px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-ink)]">
                Optional (bonus)
              </span>
              <span className="block text-[11px] text-[var(--tir-muted)] mt-0.5 leading-snug">
                Won't count toward prescribed mileage / time / adherence and won't
                be flagged as missed if skipped. If the athlete completes it, the
                actual mileage and time still get added to the report.
              </span>
            </span>
          </label>

          {/* Structure — segments for runs, blocks for strength */}
          <div className="border-t border-[var(--tir-border)] pt-4">
            {isBlockType(form.type) ? (
              <>
                <StrengthEditor
                  blocks={form.strength_blocks || []}
                  onChange={(blocks) => setField("strength_blocks", blocks)}
                />
                {athleteId && programId && workout?.id && (
                  <CoachStrengthLogView
                    athleteId={athleteId}
                    programId={programId}
                    workoutId={workout.id}
                    blocks={form.strength_blocks || []}
                  />
                )}
              </>
            ) : (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <div className="font-serif-display text-lg text-[var(--tir-ink)]">
                    Structure
                  </div>
                  <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 sm:gap-3">
                    <button
                      type="button"
                      onClick={() => addSegment("warmup")}
                      className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] font-semibold border border-[var(--tir-border)] sm:border-0 px-2 py-1.5 sm:p-0 whitespace-nowrap"
                    >
                      + Warm-up
                    </button>
                    <button
                      type="button"
                      onClick={() => addSegment("simple")}
                      className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] font-semibold border border-[var(--tir-border)] sm:border-0 px-2 py-1.5 sm:p-0 whitespace-nowrap"
                    >
                      + Segment
                    </button>
                    <button
                      type="button"
                      onClick={() => addSegment("group")}
                      data-testid="add-interval-group-btn"
                      className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] font-semibold border border-[var(--tir-border)] sm:border-0 px-2 py-1.5 sm:p-0 whitespace-nowrap"
                    >
                      + Interval block
                    </button>
                    <button
                      type="button"
                      onClick={() => addSegment("cooldown")}
                      className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] font-semibold border border-[var(--tir-border)] sm:border-0 px-2 py-1.5 sm:p-0 whitespace-nowrap"
                    >
                      + Cool-down
                    </button>
                  </div>
                </div>
                {form.segments.length === 0 ? (
                  <div className="border border-dashed border-[var(--tir-border)] py-8 text-center text-sm text-[var(--tir-muted)]">
                    {form.type === "rest"
                      ? "Rest day — no workout needed."
                      : "No structure yet. Add a segment above to get started."}
                  </div>
                ) : (
                  <DragDropContext onDragEnd={onSegmentDragEnd}>
                    <Droppable droppableId="segments" type="segment">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-3" data-testid="segments-list">
                          {form.segments.map((seg, idx) => (
                            <Draggable key={seg.id || `s${idx}`} draggableId={`seg-${seg.id || idx}`} index={idx}>
                              {(dp, snapshot) => (
                                <div ref={dp.innerRef} {...dp.draggableProps} className={snapshot.isDragging ? "shadow-xl ring-2 ring-[var(--tir-burgundy)]/40" : ""}>
                                  <SegmentRow
                                    seg={seg}
                                    onChange={(u) => updateSegment(idx, u)}
                                    onDelete={() => deleteSegment(idx)}
                                    athletePaces={athletePaces}
                                    dragHandleProps={dp.dragHandleProps}
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
                {(form.strength_blocks || []).length > 0 && (
                  <div className="mt-5 pt-4 border-t border-dashed border-[var(--tir-border)]" data-testid="run-type-strength-blocks">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[var(--tir-muted)] mb-2">
                      This workout also carries strength blocks — set Type to <strong>Strength</strong> if it's a pure lift.
                    </p>
                    <StrengthEditor
                      blocks={form.strength_blocks || []}
                      onChange={(blocks) => setField("strength_blocks", blocks)}
                    />
                    {athleteId && programId && workout?.id && (
                      <CoachStrengthLogView
                        athleteId={athleteId}
                        programId={programId}
                        workoutId={workout.id}
                        blocks={form.strength_blocks || []}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Strides / hill surges — available on every running workout
              (easy, long, recovery, tempo, threshold, speed, race, etc).
              Strength / mobility / cross / rest never get the toggle. */}
          {RUNNING_TYPES.has((form.type || "").toLowerCase()) && (
            <div className="border-t border-[var(--tir-border)] pt-4">
              <div className="flex items-center gap-5 flex-wrap">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.add_strides && (form.strides_kind || "strides") === "strides"}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setField("add_strides", true);
                        setField("strides_kind", "strides");
                      } else {
                        setField("add_strides", false);
                      }
                    }}
                    data-testid="add-strides-toggle"
                    className="h-4 w-4 accent-[var(--tir-burgundy)]"
                  />
                  <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-ink)]">
                    Add strides
                  </span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!form.add_strides && form.strides_kind === "hill_surges"}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setField("add_strides", true);
                        setField("strides_kind", "hill_surges");
                      } else {
                        setField("add_strides", false);
                      }
                    }}
                    data-testid="add-hill-surges-toggle"
                    className="h-4 w-4 accent-[var(--tir-burgundy)]"
                  />
                  <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-ink)]">
                    Add hill surges
                  </span>
                </label>
              </div>
              {form.add_strides && (
                <div className="mt-3 ml-6 flex items-center gap-2 text-sm">
                  <input
                    type="text"
                    inputMode="text"
                    maxLength={12}
                    value={form.strides_count ?? ""}
                    onChange={(e) =>
                      setField(
                        "strides_count",
                        e.target.value === "" ? null : e.target.value
                      )
                    }
                    placeholder="4 or 4-6"
                    data-testid="strides-count-input"
                    className="w-20 bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-2 py-1 text-center font-mono-tir focus:outline-none"
                  />
                  <span className="text-[var(--tir-muted)]">
                    × {form.strides_kind === "hill_surges" ? "hill surges" : "strides"} in the last half of the workout
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Session note */}
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
              Session note (athlete-facing tip)
            </label>
            <RichTextEditor
              value={form.session_note || ""}
              onChange={(v) => setField("session_note", v)}
              rows={2}
              testid="session-note-input"
              placeholder="Focus on quick, light feet and drive the knees."
            />
          </div>

          {/* External + audio links */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Workout link (optional)
              </label>
              <input
                type="url"
                value={form.external_link || ""}
                onChange={(e) => setField("external_link", e.target.value)}
                placeholder="https://the-intuitive-runner.fitterapp.app/workouts/…"
                data-testid="workout-link-input"
                className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-sm focus:outline-none font-mono-tir"
              />
              <div className="text-[10px] text-[var(--tir-muted)] mt-1 italic">
                e.g. fitterapp workout — athlete clicks to follow along in the app.
              </div>
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Audio coaching link (optional)
              </label>
              <input
                type="url"
                value={form.audio_link || ""}
                onChange={(e) => setField("audio_link", e.target.value)}
                placeholder="https://… (mp3, podcast episode, drive link)"
                data-testid="workout-audio-input"
                className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-sm focus:outline-none font-mono-tir"
              />
              <div className="text-[10px] text-[var(--tir-muted)] mt-1 italic">
                Audio-coached run for athletes who want to listen along.
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 p-4 sm:p-6 border-t border-[var(--tir-border)] sticky bottom-0 bg-[var(--tir-ivory)]">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                if (window.confirm("Delete this workout?")) onDelete();
              }}
              data-testid="workout-delete-btn"
              className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)]"
            >
              <Trash2 size={14} /> Delete
            </button>
            {onDuplicate && weekDays && weekDays.length > 1 && (
              <DuplicateToDaysButton
                currentDayLabel={currentDayLabel}
                weekDays={weekDays}
                allWeeks={allWeeks}
                currentWeekNumber={currentWeekNumber}
                onDuplicate={(dayIds, opts) => onDuplicate(form, dayIds, opts)}
              />
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="text-sm tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)] px-4 py-2"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              data-testid="workout-save-btn"
              className="bg-[var(--tir-burgundy)] text-white px-6 py-2.5 text-sm tracking-[0.22em] uppercase font-semibold hover:bg-[var(--tir-burgundy-dark)] transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function DuplicateToDaysButton({ currentDayLabel, weekDays, allWeeks, currentWeekNumber, onDuplicate }) {
  const [open, setOpen] = useState(false);
  const [selectedDays, setSelectedDays] = useState({}); // dayId -> bool
  const [selectedWeeks, setSelectedWeeks] = useState({}); // week_number -> bool
  // Whether each duplicate should also be flagged as an "OR" alternative.
  // Default OFF — the common case is fanning a routine (mobility, easy run)
  // across the week without OR-ing it with whatever else is there. Coaches
  // who want an OR pattern (e.g. "Group run OR Hill repeats") tick this on.
  const [asAlternative, setAsAlternative] = useState(false);

  const otherDays = (weekDays || []).filter((d) => d.label !== currentDayLabel);
  const otherWeeks = (allWeeks || []).filter((w) => w.week_number !== currentWeekNumber);
  // Current week is implicit: if the coach also wants the same day in the
  // current week, they tick that day above. Cross-week dropdown only lists
  // OTHER weeks. If they pick only weeks and no extra days, we assume they
  // mean "same day this workout is on" (currentDayLabel).
  const anyDay = Object.values(selectedDays).some(Boolean);
  const anyWeek = Object.values(selectedWeeks).some(Boolean);
  const canApply = anyDay || anyWeek;

  const apply = () => {
    const dayIds = Object.entries(selectedDays).filter(([, v]) => v).map(([k]) => k);
    const extraWeekNumbers = Object.entries(selectedWeeks)
      .filter(([, v]) => v)
      .map(([k]) => Number(k));
    onDuplicate(dayIds, { asAlternative, extraWeekNumbers });
    setOpen(false);
    setSelectedDays({});
    setSelectedWeeks({});
    setAsAlternative(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="workout-duplicate-btn"
        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-navy)]"
      >
        <Copy size={14} /> Duplicate to…
      </button>
      {open && (
        <div
          className="absolute bottom-full mb-2 left-0 z-50 bg-white border border-[var(--tir-border)] shadow-lg p-3 min-w-[260px] max-w-[320px]"
          data-testid="duplicate-days-picker"
        >
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-2">
            Copy to which days?
          </div>
          <div className="space-y-1">
            {otherDays.map((d) => (
              <label
                key={d.id}
                className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[var(--tir-ivory)] px-1 py-0.5"
              >
                <input
                  type="checkbox"
                  checked={!!selectedDays[d.id]}
                  onChange={(e) =>
                    setSelectedDays((prev) => ({ ...prev, [d.id]: e.target.checked }))
                  }
                  data-testid={`duplicate-target-${d.label}`}
                />
                <span>{d.label}</span>
              </label>
            ))}
          </div>
          {otherWeeks.length > 0 && (
            <div className="mt-3 pt-2 border-t border-[var(--tir-border)]">
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-2">
                Also in weeks
                <span className="ml-2 normal-case tracking-normal text-[10px] italic text-[var(--tir-muted)]/70 font-normal">
                  — same day(s)
                </span>
              </div>
              <WeekRangePicker
                otherWeeks={otherWeeks}
                selectedWeeks={selectedWeeks}
                setSelectedWeeks={setSelectedWeeks}
              />
              <div className="space-y-0.5 max-h-[160px] overflow-y-auto pr-1" data-testid="duplicate-weeks-list">
                {otherWeeks.map((w) => (
                  <label
                    key={w.week_number}
                    className="flex items-center gap-2 text-[12px] cursor-pointer hover:bg-[var(--tir-ivory)] px-1 py-0.5"
                  >
                    <input
                      type="checkbox"
                      checked={!!selectedWeeks[w.week_number]}
                      onChange={(e) =>
                        setSelectedWeeks((prev) => ({
                          ...prev,
                          [w.week_number]: e.target.checked,
                        }))
                      }
                      data-testid={`duplicate-target-week-${w.week_number}`}
                    />
                    <span className="font-mono-tir text-[var(--tir-ink)]">W{w.week_number}</span>
                    {w.phase_label && (
                      <span className="text-[10px] text-[var(--tir-muted)] truncate">
                        {w.phase_label}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}
          <label
            className="flex items-start gap-2 mt-3 pt-2 border-t border-[var(--tir-border)] text-[11px] cursor-pointer hover:bg-[var(--tir-ivory)] px-1 py-1"
            data-testid="duplicate-as-alternative-row"
          >
            <input
              type="checkbox"
              checked={asAlternative}
              onChange={(e) => setAsAlternative(e.target.checked)}
              className="mt-0.5 accent-[var(--tir-burgundy)]"
              data-testid="duplicate-as-alternative-toggle"
            />
            <span className="leading-snug">
              <span className="block font-semibold tracking-[0.18em] uppercase text-[10px] text-[var(--tir-ink)]">
                Keep as alternative (OR)
              </span>
              <span className="block text-[10px] text-[var(--tir-muted)] italic">
                Each copy shows as an OR option on the target day.
              </span>
            </span>
          </label>
          <div className="flex items-center justify-end gap-2 mt-3">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setSelectedDays({});
                setSelectedWeeks({});
                setAsAlternative(false);
              }}
              className="text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)] px-2 py-1"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={apply}
              disabled={!canApply}
              data-testid="duplicate-apply-btn"
              className="bg-[var(--tir-navy)] text-white px-3 py-1 text-[10px] tracking-[0.22em] uppercase font-semibold hover:bg-[#152a45] transition-colors disabled:opacity-40"
            >
              Duplicate
            </button>
          </div>
        </div>
      )}
    </div>
  );
}



/**
 * Range shortcut for the cross-week selector. Coaches building a base block
 * that runs unchanged for 6+ weeks can tick "W2 → W7" in one move instead
 * of checking each week individually. Buttons:
 *   - "Select range" → marks every week within the [from, to] range
 *   - "All" → ticks every week in `otherWeeks`
 *   - "Clear" → unticks all
 * Range bounds clamp to the program's actual week range so we never set a
 * non-existent week_number on the parent state.
 */
function WeekRangePicker({ otherWeeks, selectedWeeks, setSelectedWeeks }) {
  const nums = otherWeeks.map((w) => w.week_number);
  const minWk = Math.min(...nums);
  const maxWk = Math.max(...nums);
  const [from, setFrom] = useState(String(minWk));
  const [to, setTo] = useState(String(maxWk));

  const applyRange = () => {
    const f = Math.max(minWk, Math.min(maxWk, Number(from) || minWk));
    const t = Math.max(minWk, Math.min(maxWk, Number(to) || maxWk));
    const lo = Math.min(f, t);
    const hi = Math.max(f, t);
    setSelectedWeeks((prev) => {
      const next = { ...prev };
      for (const w of otherWeeks) {
        if (w.week_number >= lo && w.week_number <= hi) next[w.week_number] = true;
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedWeeks(() => {
      const next = {};
      for (const w of otherWeeks) next[w.week_number] = true;
      return next;
    });
  };

  const clear = () => setSelectedWeeks({});

  return (
    <div
      className="flex items-center gap-1.5 mb-2 text-[11px]"
      data-testid="duplicate-weeks-range"
    >
      <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)]">Range</span>
      <span className="font-mono-tir text-[var(--tir-muted)]">W</span>
      <input
        type="number"
        min={minWk}
        max={maxWk}
        value={from}
        onChange={(e) => setFrom(e.target.value)}
        data-testid="duplicate-weeks-range-from"
        className="w-12 bg-white border border-[var(--tir-border)] focus:border-[var(--tir-navy)] px-1 py-0.5 text-center font-mono-tir text-[11px] focus:outline-none"
      />
      <span className="text-[var(--tir-muted)]">→ W</span>
      <input
        type="number"
        min={minWk}
        max={maxWk}
        value={to}
        onChange={(e) => setTo(e.target.value)}
        data-testid="duplicate-weeks-range-to"
        className="w-12 bg-white border border-[var(--tir-border)] focus:border-[var(--tir-navy)] px-1 py-0.5 text-center font-mono-tir text-[11px] focus:outline-none"
      />
      <button
        type="button"
        onClick={applyRange}
        data-testid="duplicate-weeks-range-apply"
        className="ml-1 text-[9px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-navy)] hover:text-[var(--tir-burgundy)] px-1.5 py-0.5 border border-[var(--tir-border)] hover:border-[var(--tir-navy)]"
      >
        Select
      </button>
      <span className="flex-1" />
      <button
        type="button"
        onClick={selectAll}
        data-testid="duplicate-weeks-select-all"
        className="text-[9px] tracking-[0.18em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-navy)] px-1"
      >
        All
      </button>
      <span className="text-[var(--tir-border)]">·</span>
      <button
        type="button"
        onClick={clear}
        data-testid="duplicate-weeks-clear"
        className="text-[9px] tracking-[0.18em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] px-1"
      >
        Clear
      </button>
    </div>
  );
}



/**
 * Tiny inline pace estimate that appears under the "total time or distance"
 * input when the coach has prescribed time-only ("60 min easy") on a running
 * workout. Shows what that converts to in km at the athlete's actual easy
 * pace so the coach can sanity-check the workload before saving.
 *
 * The estimate is based on the HEADLINE only (the text in the input above) —
 * not the full segment breakdown — because this hint is about "what does
 * this prescription mean for THIS athlete in km", purely a teaching aid for
 * the coach typing time-only intervals. Segment-level totals are surfaced
 * elsewhere (weekly summary).
 *
 * Hidden when:
 *   - The headline already mentions km/miles/m (nothing to estimate)
 *   - The workout isn't a running type (strength/mobility/rest)
 *   - There are no minutes parsed (rest day, empty headline)
 *
 * Falls back gracefully if no `athletePaces` are available (uses per-type
 * defaults — same defaults `workoutVolume` uses).
 */
function PaceEstimateHint({ form, athletePaces }) {
  if (!form) return null;
  const type = (form.type || "").toLowerCase();
  const runningTypes = [
    "easy", "long", "recovery", "tempo", "marathon",
    "speed", "interval", "intervals", "threshold", "race",
    "run", "strength_run",
  ];
  if (!runningTypes.includes(type)) return null;
  const headline = String(form.duration_or_distance || "");
  // Already has an explicit distance unit → no estimate needed.
  if (/\d+\.?\d*\s*(?:km|kilometers?|kilometres?|k\b|mi\b|miles?|m\b)/i.test(headline)) {
    return null;
  }
  // Parse minutes out of the headline directly (matches `parseDurationOrDistance`
  // in coaching.js but inline so we don't expose that helper).
  let minutes = 0;
  const hhmm = /^(\d+):(\d{2})$/.exec(headline.trim());
  if (hhmm) {
    minutes = Number(hhmm[1]) * 60 + Number(hhmm[2]);
  } else {
    const m = /([\d.]+)\s*(min|minutes?|hr|hrs|hours?|h)\b/i.exec(headline);
    if (m) {
      const n = Number(m[1]);
      const unit = (m[2] || "min").toLowerCase();
      minutes = unit.startsWith("h") ? n * 60 : n;
    }
  }
  if (minutes <= 0) return null;

  // Use a placeholder workout with ONLY the minutes+type set so workoutVolume's
  // time→km conversion runs cleanly (no segment interference).
  const placeholder = {
    type: form.type,
    duration_or_distance: `${minutes} min`,
    segments: [],
    strength_blocks: [],
  };
  const vol = workoutVolume(placeholder, athletePaces);
  if (!vol || vol.km <= 0) return null;

  const kmStr = vol.km >= 10 ? vol.km.toFixed(0) : vol.km.toFixed(1);
  const paceKind =
    type === "tempo" || type === "marathon"
      ? "tempo"
      : type === "speed" || type === "interval" || type === "intervals"
      ? "interval"
      : type === "threshold"
      ? "threshold"
      : "easy";
  const paceOwner = athletePaces ? "this athlete's" : "default";
  return (
    <div
      className="mt-1 text-[10.5px] text-[var(--tir-muted)] italic flex items-center gap-1 flex-wrap"
      data-testid="workout-pace-estimate"
      title={`Estimate from ${minutes} min at ${paceOwner} ${paceKind} pace`}
    >
      <span className="text-[var(--tir-burgundy)]">≈</span>
      <span className="font-mono-tir font-semibold text-[var(--tir-ink)]">
        {kmStr} km
      </span>
      <span>
        at {paceOwner} {paceKind} pace
      </span>
    </div>
  );
}
